import { useState } from 'react';
import styled from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import stripHtml from 'string-strip-html';
import { useDeepCompareEffect, useEffectOnce } from 'react-use';
import { useQuery } from 'react-query';
import { useLocation } from 'react-router-dom';
import { createConversation } from 'actions/conversations';
import { createNewConversationPlaceholder, updateNewConversationPlaceholderEntities } from 'actions/ui';
import { flatten, isEmpty, pickBy, trim, uniq } from 'lodash';
import { SERGIC_OFFER } from '../../../constants';
import { getDistributionLists, searchPlaceContactsV2 } from 'api/places';
import NewConversationMembers from 'components/NewConversationMembers';
import MessageForm from 'components/MessageForm';
import DistributionListCard from 'components/DistributionListCard';
import NewConversationResultMemberCard from 'components/NewConversationResultMemberCard';
import { List, media, Subtitle as RawSubtitle } from 'ui';
import { BuildingDistributionListsPickerModal, DistributionListContactPickerModal } from 'components/Conversations';
import { getSelectedPlace } from '../../../selectors';

const Subtitle = styled(RawSubtitle)`
    font-size: ${({ theme }) => theme.size(-1)};
    color: ${({ theme }) => theme.textLight};
    font-weight: 400;
    text-transform: uppercase;
`;

const NewConversationContainer = styled.div`
    position: relative;
    padding-top: 58px;

    ${media.tablet`padding-top: 0;`};
`;

const SpecialStates = styled.div`
    color: ${({ theme }) => theme.textLight};
`;

const ResultsContainer = styled.div`
    background-color: #fff;
    padding: ${({ theme }) => theme.spacing()};
    border: 1px solid ${({ theme }) => theme.separator};
    margin-right: ${({ theme }) => theme.spacing()};
    margin-left: ${({ theme }) => theme.spacing()};
    border-top: 0;
    margin-top: -1px;

    ${media.tablet`
    display: flex;
    margin-top: 1px;
  `};

    > div {
        flex: 1;
    }
`;

const DistributionListsList = styled(List)`
    display: flex;
    margin-left: ${({ theme }) => theme.spacing(-1)};
    margin-top: ${({ theme }) => theme.spacing(-1)};
    width: calc(100% + ${({ theme }) => theme.spacing()});
    flex-wrap: wrap;
    flex-direction: column;

    > li {
        padding-left: ${({ theme }) => theme.spacing()};
        padding-top: ${({ theme }) => theme.spacing()};
        flex: 0 0 100%;

        ${media.desktop`
      flex: 0 0 33.33%;
    `};
    }
`;

const NewConversation = () => {
   const { t } = useTranslation('newConversation');
   const dispatch = useDispatch();
   const location = useLocation();
   const place = useSelector(getSelectedPlace);

   const selectedPlaceId = useSelector(state => state.ui.selectedPlaceId);
   const newConversation = useSelector(state => state.ui.newConversation);

   const [attachments, setAttachments] = useState([]);
   const [search, setSearch] = useState('');
   const [newConversationName, setNewConversationName] = useState('');
   const [selectedDistributionList, setSelectedDistributionList] = useState(null);
   const [isPickingBuildingDistributionLists, setIsPickingBuildingDistributionLists] = useState(false);

   const [isSubmitting, setIsSubmitting] = useState(false);
   const [body, setBody] = useState('');

   const { data: contactsData, isFetching, isFetched } = useQuery(
      ['searchPlaceContacts', selectedPlaceId, search, place?.canTalk],
      () =>
         searchPlaceContactsV2(
            selectedPlaceId,
            place?.canTalk === false
               ? {
                  'filter[search]': search,
                  '[sergic_partner]': '',
               }
               : {
                  'filter[search]': search,
               },
         ),
      {
         shouldKeepPreviousData: true,
      },
   );

   const contacts = contactsData?.data || [];

   const { data: distributionsListData } = useQuery(['distributionsList', selectedPlaceId], () =>
      getDistributionLists(selectedPlaceId),
   );
   const distributionLists = distributionsListData?.data || [];
   const filteredDistributionLists = distributionLists.filter(
      ({ displayName, id }) => Boolean(id) && displayName.toLowerCase().indexOf((search || '').toLowerCase()) > -1,
   );
   const buildingDistributionLists = distributionLists.filter(({ kind }) => kind === 'building');

   const handleAddAttachment = attachment => {
      setAttachments([...attachments, attachment]);
   };

   const handleRemoveAttachment = attachmentId => {
      setAttachments(attachments.filter(({ id }) => id !== attachmentId));
   };

   const handleAddEntity = (entity, type, force) => {
      if (entity.canPick && !force) {
         setSelectedDistributionList(entity);
         return;
      }

      dispatch(
         updateNewConversationPlaceholderEntities({
            entities: [...(newConversation?.entities || []), { ...entity, type }],
         }),
      );

      setSearch('');
   };

   const handleAddEntities = data => {
      dispatch(
         updateNewConversationPlaceholderEntities({
            entities: [...(newConversation?.entities || []), ...data.map(({ entity, type }) => ({ ...entity, type }))],
         }),
      );

      setSearch('');
   };

   const handleDeleteEntity = (id, type) => {
      dispatch(
         updateNewConversationPlaceholderEntities({
            entities: (newConversation?.entities || []).filter(entity => entity.id !== id || entity.type !== type),
         }),
      );
   };

   const handleSubmitConversation = async e => {
      e.preventDefault();
      setIsSubmitting(true);

      const sanitizedBody = trim(stripHtml(body));

      const conversation = pickBy({
         displayName: newConversationName,
         memberIds: uniq(
            flatten(
               (newConversation?.entities || []).map(({ type, id, userIds }) => (type === 'MEMBER' ? id : userIds)),
            ),
         ),
      });

      let message = {};

      if (Boolean(sanitizedBody)) {
         message = { ...message, body: sanitizedBody };
      }

      if (attachments.length > 0) {
         message = {
            ...message,
            attachmentIds: attachments.map(({ id }) => id),
         };
      }

      if (attachments.length === 0 && !Boolean(sanitizedBody)) {
         return;
      }

      await dispatch(createConversation(selectedPlaceId, conversation, message));
      setIsSubmitting(false);
   };

   useEffectOnce(() => {
      const { reset, members } = location.state || {};

      if (Boolean(reset) || !newConversation) {
         dispatch(createNewConversationPlaceholder());
      }

      if (Boolean(reset) && Boolean(members)) {
         dispatch(
            updateNewConversationPlaceholderEntities({
               entities: (members || []).map(member => ({
                  ...member,
                  type: 'MEMBER',
               })),
            }),
         );
      }
   });

   useDeepCompareEffect(() => {
      setSelectedDistributionList(null);
   }, [{ selectedPlaceId }]);

   return (
      <>
         <NewConversationContainer>
            <NewConversationMembers
               entities={newConversation?.entities || []}
               onDeleteEntity={handleDeleteEntity}
               searchInputValue={search}
               onUpdateSearchInput={e => setSearch(e.target.value)}
               newConversationName={newConversationName}
               onChangeNewConversationName={e => setNewConversationName(e.target.value)}
            />

            <ResultsContainer>
               <div>
                  <Subtitle bareTop>{t('communications:contacts')}</Subtitle>

                  <SpecialStates>
                     {isFetching && contacts.length === 0 && t('misc:loading')}
                     {isFetched && contacts.length === 0 && t('noResult')}
                  </SpecialStates>

                  <List>
                     {contacts.map(member => (
                        <li key={member.id} style={{ marginTop: 24, marginLeft: 8, paddingRight: 16 }}>
                           <NewConversationResultMemberCard
                              {...member}
                              isActive={
                                 (newConversation?.entities || [])
                                    .filter(({ type }) => type === 'MEMBER')
                                    .map(({ id }) => id)
                                    .indexOf(member.id) > -1
                              }
                              onAdd={() => handleAddEntity(member, 'MEMBER')}
                           />
                        </li>
                     ))}
                  </List>
               </div>

               {SERGIC_OFFER !== 'DIR' && (
                  <div>
                     <Subtitle bareTop>{t('diffusion')}</Subtitle>

                     {filteredDistributionLists.length > 0 && (
                        <DistributionListsList>
                           {filteredDistributionLists.map(distributionList => (
                              <li key={distributionList.id}>
                                 <DistributionListCard
                                    displayName={distributionList.displayName}
                                    count={distributionList.userCount}
                                    onAdd={() => handleAddEntity(distributionList, 'DISTRIBUTION_LIST')}
                                    isActive={
                                       (newConversation?.entities || [])
                                          .filter(({ type }) => type === 'DISTRIBUTION_LIST')
                                          .map(({ id }) => id)
                                          .indexOf(distributionList.id) > -1
                                    }
                                 />
                              </li>
                           ))}

                           {buildingDistributionLists.length > 0 && (
                              <li>
                                 <DistributionListCard
                                    displayName={t('buildings')}
                                    onAdd={() => setIsPickingBuildingDistributionLists(true)}
                                    countKind='distributionLists'
                                    count={buildingDistributionLists.length}
                                 />
                              </li>
                           )}
                        </DistributionListsList>
                     )}

                     {distributionLists.length > 0 && filteredDistributionLists.length === 0 && (
                        <SpecialStates>{t('noResult')}</SpecialStates>
                     )}
                  </div>
               )}
            </ResultsContainer>

            <MessageForm
               attachments={attachments}
               isDisabled={(newConversation?.entities ?? []).length === 0 || isEmpty(newConversationName.trim())}
               value={body}
               onChange={body => setBody(body)}
               onSubmit={handleSubmitConversation}
               onAddAttachment={handleAddAttachment}
               onRemoveAttachment={handleRemoveAttachment}
               isSubmitting={isSubmitting}
            />
         </NewConversationContainer>

         {Boolean(selectedDistributionList) && (
            <DistributionListContactPickerModal
               onRequestClose={() => setSelectedDistributionList(null)}
               distributionList={selectedDistributionList}
               onAddEntities={handleAddEntities}
            />
         )}

         {isPickingBuildingDistributionLists && (
            <BuildingDistributionListsPickerModal
               initialSelectedDistributionsList={(newConversation?.entities || []).filter(
                  ({ type, kind }) => type === 'DISTRIBUTION_LIST' && kind === 'building',
               )}
               onRequestClose={() => setIsPickingBuildingDistributionLists(null)}
               onAddEntities={handleAddEntities}
            />
         )}
      </>
   );
};

export default NewConversation;
